import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { formatInstant, formatUtc, addUtc, zonedDateBoundary } from '../../../../utilities/dateTime';
import isEqual from 'lodash/isEqual';
import {
    Type, MapPin, Calendar, Clock, FileText, Info, CheckCircle2, X as CloseIcon, AlertCircle
} from 'lucide-react';

import { useAuth } from '../../../../firebase/AuthProvider';
import { getCalendarAttendeeOptions, updateEvent } from '../../../../services/calendarService/EventService';
import { getTaskById } from '../../../../services/taskServices/TaskService';
import { AllEvent } from '../../../../interfaces/identity/calendar/Events';
import { CalendarDirectoryPerson } from '../../../../interfaces/calendar/calendarDirectoryOptions';
import Team from '../../../../interfaces/identity/Team';
import ParticipantSelectDropdown from '../../../ui/Dropdown/ParticipantSelectDropdown';
import { normalizeInviteeId } from '../../../ui/Dropdown/participantInviteSelection';
import ScopeModal from './ScopeModal';
import CustomInput from '../CustomInput';
import TimePicker from '../TimePicker';
import AsyncTaskDropdown, { CalendarTaskSelection } from '../AsyncTaskDropdown';
import { mapToUnifiedAttendees } from '../../../../utilities/calendar/calendarMapper';
import RecurrenceSelector from './RecurrenceSelector';
import { wallTimeInstant, dateCarrier, wallTimePreview } from '../../../../utilities/calendar/calendarDisplayTime';
import { parseUtcTimestamp } from '../../../../utilities/utcDateTime';

interface EditEventProps {
    event: AllEvent;
    onClose?: () => void;
    onEventCreated?: () => void;
    displayTimeZone?: string;
}

export default function EditEvent({ event, onClose, onEventCreated, displayTimeZone }: EditEventProps) {
    const { currentUser } = useAuth();
    const userId = (currentUser as any)?.uid;
    const token = (currentUser as any)?.accessToken;
    const excludedInviteeUserIds = useMemo(
        () => userId ? [String(userId)] : [],
        [userId]
    );

    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState<CalendarDirectoryPerson[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [linkedTask, setLinkedTask] = useState<CalendarTaskSelection | null>(null);
    const [isLinkedTaskLoading, setIsLinkedTaskLoading] = useState(Boolean(event?.taskId));
    const [linkedTaskLoadFailed, setLinkedTaskLoadFailed] = useState(false);
    const [showEditScopeModal, setShowEditScopeModal] = useState(false);
    const [pendingFormData, setPendingFormData] = useState<any>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const currentTimeZone = (displayTimeZone && (event.recurrenceRule || event.allDayStart) && (event.time_zone || event.timeZoneId)) || displayTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const MAX_NOTES_LENGTH = 1000;
    const isAllDay = Boolean(event.allDayStart);
    const [lastDay, setLastDay] = useState(event.allDayEndExclusive ? formatUtc(addUtc(dateCarrier(event.allDayEndExclusive!), -1), 'yyyy-MM-dd') : '');

    // Escape closes this modal, but lets the nested recurrence scope modal
    // handle Escape first when it is open.
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !showEditScopeModal) {
                onClose?.();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [onClose, showEditScopeModal]);

    const [selectedDate, setSelectedDate] = useState<string>(
        event.allDayStart || (event?.startTime ? formatInstant(parseUtcTimestamp(event.startTime)!, "yyyy-MM-dd", currentTimeZone) : formatInstant(new Date(), "yyyy-MM-dd"))
    );

    // FIX: Parse the incoming unified attendees array into our form state arrays
    const incomingAttendees = useMemo(() => event?.attendees || [], [event?.attendees]);



    const mappedParticipantIds = incomingAttendees
        .filter((a: any) => a.type === "EMPLOYEE" || a.type === "WAIVER_PARTICIPANT")
        .map((a: any) => String(a.ref))
        .filter((inviteeId: string) =>
            normalizeInviteeId(inviteeId) !== normalizeInviteeId(userId ?? "")
        );

    const mappedExternalEmails = incomingAttendees
        .filter((a: any) => a.type === "EXTERNAL")
        .map((a: any) => a.email)
        .filter((email: any) => email && email !== 'undefined' && email !== null);

    const initialFormState = {
        title: event?.title || "",
        startTime: event?.startTime ? formatInstant(parseUtcTimestamp(event.startTime)!, "h:mm a", currentTimeZone) : "09:00 AM",
        endTime: event?.endTime ? formatInstant(parseUtcTimestamp(event.endTime)!, "h:mm a", currentTimeZone) : "09:30 AM",
        description: event?.description || "",
        location: event?.location || "Waivera",
        participantUserIds: mappedParticipantIds,
        individualUserIds: mappedParticipantIds,
        selectedTeamIds: [] as number[],
        externalUserEmails: mappedExternalEmails,
        taskId: event?.taskId || 0,
        isSprout: event?.isSprout || false,
        recurrenceRule: event?.recurrenceRule,
        parentEventId: event?.parentEventId
    };

    const [formData, setFormData] = useState(initialFormState);
    const isDirty = !isEqual(initialFormState, formData) || selectedDate !== (event.allDayStart || formatInstant(parseUtcTimestamp(event?.startTime)!, 'yyyy-MM-dd', currentTimeZone)) || (isAllDay && lastDay !== formatUtc(addUtc(dateCarrier(event.allDayEndExclusive!), -1), 'yyyy-MM-dd'));

    const getTaskParticipantId = (
        task?: CalendarTaskSelection | null
    ): string | null => {
        const participantId = task?.participant?.id;
        return participantId == null ? null : String(participantId).toLowerCase();
    };

    const getTaskParticipantName = (
        task?: CalendarTaskSelection | null
    ): string => {
        const participant = task?.participant;
        if (!participant) return "the task participant";

        const name = `${participant.firstName ?? ""} ${participant.lastName ?? ""}`.trim();
        return name || "the task participant";
    };

    // Employees and the one already-linked task are loaded in parallel. The
    // task lookup is intentionally independent of autocomplete results so invite
    // restrictions are correct before the user can edit invitees.
    useEffect(() => {
        if (!token) return;

        const init = async () => {
            setLoading(true);
            setIsLinkedTaskLoading(Boolean(event?.taskId));
            setLinkedTaskLoadFailed(false);

            try {
                const employeePromise = getCalendarAttendeeOptions(token, "edit", event.id);
                const taskPromise = event?.taskId
                    ? getTaskById(String(event.taskId), token)
                    : Promise.resolve(null);

                const [emp, hydratedTask] = await Promise.all([
                    employeePromise,
                    taskPromise
                ]);

                const existingPeople: CalendarDirectoryPerson[] = incomingAttendees
                    .filter((attendee: any) => attendee.type === "EMPLOYEE" || attendee.type === "WAIVER_PARTICIPANT")
                    .filter((attendee: any) => attendee.ref)
                    .map((attendee: any) => ({
                        userId: String(attendee.ref),
                        userName: attendee.displayName || attendee.email || "Existing attendee",
                        email: attendee.email,
                        type: attendee.type === "EMPLOYEE" ? "Employee" : "Participant",
                        roles: [],
                        teamIds: [],
                    }));
                const directoryIds = new Set(emp.people.map((person) => String(person.userId).toLowerCase()));
                setEmployees([
                    ...emp.people,
                    ...existingPeople.filter((person) => !directoryIds.has(String(person.userId).toLowerCase())),
                ]);
                setTeams(emp.teams);

                if (event?.taskId) {
                    if (hydratedTask) {
                        setLinkedTask(hydratedTask);
                    } else {
                        setLinkedTask(null);
                        setLinkedTaskLoadFailed(true);
                    }
                } else {
                    setLinkedTask(null);
                }
            } catch (error) {
                console.error("Failed to load EditEvent initial data", error);

                if (event?.taskId) {
                    setLinkedTask(null);
                    setLinkedTaskLoadFailed(true);
                }
            } finally {
                setLoading(false);
                setIsLinkedTaskLoading(false);
            }
        };

        init();
    }, [event?.taskId, event.id, incomingAttendees, token]);

    // SYNC VALIDATION: end after start + prevent selecting past date/time
    useEffect(() => {
        try {
            if (isAllDay) {
                const duration = (+dateCarrier(lastDay) - +dateCarrier(selectedDate)) / 86400000 + 1;
                if (!Number.isFinite(duration) || duration < 1 || duration > 366) throw new Error('Choose an all-day range of at most one year.');
                setErrors(prev => { const next = { ...prev }; delete next.time; return next; }); return;
            }
            const start = wallTimeInstant(selectedDate, formData.startTime, currentTimeZone, event.startTime);
            const end = wallTimeInstant(selectedDate, formData.endTime, currentTimeZone, event.endTime);
            const now = new Date();
            const today = formatInstant(now, "yyyy-MM-dd", currentTimeZone);

            if (selectedDate < today) {
                setErrors(prev => ({ ...prev, time: "Meeting date cannot be in the past" }));
                return;
            }

            if (Math.floor(+end / 60000) <= Math.floor(+start / 60000)) {
                setErrors(prev => ({ ...prev, time: "End time must be after start time" }));
                return;
            }

            // Compare by minute to avoid false "past" errors caused by seconds precision
            if (Math.floor(+start / 60000) < Math.floor(+now / 60000)) {
                setErrors(prev => ({ ...prev, time: "Start time cannot be in the past" }));
                return;
            }

            if (Math.floor(+end / 60000) < Math.floor(+now / 60000)) {
                setErrors(prev => ({ ...prev, time: "End time cannot be in the past" }));
                return;
            }

            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.time;
                return newErrors;
            });
        } catch (err) {
            setErrors(prev => ({ ...prev, time: err instanceof Error ? err.message : "Choose a valid calendar time." }));
        }
    }, [formData.startTime, formData.endTime, selectedDate, currentTimeZone, isAllDay, lastDay, event.startTime, event.endTime]);

    const handleStartTimeChange = (newStartTime: string) => {
        const start = wallTimeInstant("2000-01-01", newStartTime, "UTC");
        const newEndTime = formatUtc(addUtc(start, 30, "minutes"), "h:mm a");

        setFormData(prev => ({ ...prev, startTime: newStartTime, endTime: newEndTime }));
    };

    // --- TWO-WAY CONSTRAINT LOGIC ---

    const allWaiverParticipantIds = useMemo(() => {
        return employees.filter(emp => {
            const isEmployee = emp.type?.toLowerCase() === 'employee' ||
                (emp as any).userType?.toLowerCase() === 'employee' ||
                !emp.type;
            return !isEmployee;
        }).map(emp => String(emp.userId).toLowerCase());
    }, [employees]);

    const activeWaiverParticipantId = useMemo(() => {
        return formData.participantUserIds.find((id: any) =>
            allWaiverParticipantIds.includes(String(id).toLowerCase())
        );
    }, [formData.participantUserIds, allWaiverParticipantIds]);

    const availableEmployeesToInvite = useMemo(() => {
        if (!formData.taskId) return employees;

        // Fail closed while an existing linked task is being resolved. This
        // prevents a transient unrestricted invite list from appearing.
        if (isLinkedTaskLoading || !linkedTask) {
            return [];
        }

        const lockedWaiverParticipantId = getTaskParticipantId(linkedTask);
        if (!lockedWaiverParticipantId) return employees;

        return employees.filter((employee) => {
            const employeeId = String(employee.userId).toLowerCase();
            const isWaiverParticipant =
                allWaiverParticipantIds.includes(employeeId);

            return (
                !isWaiverParticipant ||
                employeeId === lockedWaiverParticipantId
            );
        });
    }, [
        employees,
        formData.taskId,
        linkedTask,
        isLinkedTaskLoading,
        allWaiverParticipantIds
    ]);

    const lockedTaskOwnerName = useMemo(
        () => getTaskParticipantName(linkedTask),
        [linkedTask]
    );

    const activeParticipantName = useMemo(() => {
        return employees.find(
            (employee) =>
                String(employee.userId).toLowerCase() ===
                String(activeWaiverParticipantId).toLowerCase()
        )?.userName || "the selected participant";
    }, [activeWaiverParticipantId, employees]);

    // --- SUBMIT LOGIC ---

    const executeSave = async (scope: 'single' | 'future' | 'all', dataToSave: any) => {
        setLoading(true);
        try {
            // TRICK: Append scope to the ID so it routes as a query parameter to the backend
            const targetId = scope !== 'all' ? `${event.id}?scope=${scope}` : event.id;

            const eventDataToSend = {
                ...dataToSave,
                id: targetId,
                ownerUserID: userId,
                startTime: isAllDay ? zonedDateBoundary(selectedDate, currentTimeZone).toISOString() : wallTimeInstant(selectedDate, dataToSave.startTime, currentTimeZone, event.startTime).toISOString(),
                endTime: isAllDay ? zonedDateBoundary(formatUtc(addUtc(dateCarrier(lastDay), 1), 'yyyy-MM-dd'), currentTimeZone).toISOString() : wallTimeInstant(selectedDate, dataToSave.endTime, currentTimeZone, event.endTime).toISOString(),
                ...(isAllDay ? { isAllDay: true, allDayStart: selectedDate, allDayEndExclusive: formatUtc(addUtc(dateCarrier(lastDay), 1), 'yyyy-MM-dd') } : {}),
                time_zone: currentTimeZone,
                attendees: mapToUnifiedAttendees(
                    dataToSave.participantUserIds.filter(
                        (inviteeId: string) => normalizeInviteeId(inviteeId) !== normalizeInviteeId(userId ?? "")
                    ),
                    dataToSave.externalUserEmails
                ),
            };

            const result = await updateEvent(eventDataToSend as any, token);

            if (result.success) {
                toast.success("Event updated successfully!");
                onEventCreated?.();
                onClose?.();
            } else {
                toast.error(result.message || "Failed to update event");
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "An unexpected error occurred");
        } finally {
            setLoading(false);
            setShowEditScopeModal(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        let validationErrors: Record<string, string> = {};
        if (!formData.title.trim()) validationErrors.title = "Title is required";

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error("Please fix errors before saving");
            return;
        }

        if (errors.time) {
            toast.error("Please fix errors before saving");
            return;
        }

        if (event?.recurrenceRule || event?.parentEventId) {
            setPendingFormData(formData);
            setShowEditScopeModal(true);
        } else {
            executeSave('single', formData);
        }
    };

    return (
        <>
            <div className="calendar-event-modal dark-popup bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-[600px] mx-auto animate-in fade-in zoom-in-95 duration-300">
                <div className="rounded-t-3xl px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                            <div className="calendar-header-icon w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                <FileText className="text-[#7E57C2]" size={18} />
                            </div>
                            Edit Event
                        </h2>
                        <p className="text-[11px] text-[#7E57C2] font-bold uppercase tracking-[0.15em] mt-2 ml-11">
                            {formatUtc(dateCarrier(selectedDate), "EEEE, MMMM dd, yyyy")} · {currentTimeZone}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white rounded-xl text-gray-400 hover:text-[#7E57C2] shadow-sm transition-all duration-200 border border-transparent hover:border-gray-200">
                        <CloseIcon size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-8">
                    <div className="calendar-meeting-toggle flex bg-gray-100/80 p-1.5 rounded-xl w-fit mx-auto border border-gray-200/50">
                        <button type="button" onClick={() => setFormData({ ...formData, isSprout: false, location: "Waivera" })}
                            aria-pressed={!formData.isSprout}
                            className={`calendar-meeting-option px-8 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${!formData.isSprout ? 'calendar-meeting-option-active bg-white text-[#7E57C2] shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}>
                            Waivera Meeting
                        </button>
                        <button type="button" onClick={() => setFormData({ ...formData, isSprout: true, location: "" })}
                            aria-pressed={formData.isSprout}
                            className={`calendar-meeting-option px-8 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${formData.isSprout ? 'calendar-meeting-option-active bg-white text-[#7E57C2] shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}>
                            External Meeting
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-7">
                        <div className="md:col-span-2">
                            <CustomInput
                                icon={Type}
                                name="title"
                                label="Event Title"
                                placeholder="What is this meeting about?"
                                value={formData.title}
                                onChange={(e) => {
                                    setFormData({ ...formData, title: e.target.value });
                                    if (errors.title) setErrors(prev => { const n = { ...prev }; delete n.title; return n; });
                                }}
                                error={errors.title}
                            />
                        </div>

                        <div className="md:col-span-2">
                            <CustomInput icon={MapPin} name="location" label="Location" placeholder={formData.isSprout ? "Enter link or address" : "Waivera"} value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} disabled={!formData.isSprout} />
                        </div>

                        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-start">
                            <CustomInput icon={Calendar} type="date" name="date" label="Meeting Date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                            {isAllDay && <CustomInput icon={Calendar} type="date" name="lastDay" label="Last day" value={lastDay} onChange={e => setLastDay(e.target.value)} />}
                            {!isAllDay && <div className="flex flex-col gap-1">
                                <TimePicker icon={Clock} label="Start Time" value={formData.startTime} onChange={(v) => handleStartTimeChange(v as string)} />
                            </div>}
                            {!isAllDay && <div className="flex flex-col gap-1">
                                <TimePicker icon={Clock} label="End Time" value={formData.endTime} onChange={(v) => setFormData({ ...formData, endTime: v as string })} />
                            </div>}
                        </div>

                        {/* Recurrence Selector */}
                        {!isAllDay && <div className="md:col-span-2">
                            <RecurrenceSelector
                                value={formData.recurrenceRule}
                                onChange={(rule) => setFormData(prev => ({ ...prev, recurrenceRule: rule }))}
                                startTime={wallTimePreview(selectedDate, formData.startTime, currentTimeZone, event.startTime)}
                                timeZone={currentTimeZone}
                            />
                        </div>}

                        {errors.time && (
                            <div className="md:col-span-2 -mt-4 flex items-center gap-2 text-red-500 text-xs font-bold animate-in slide-in-from-top-1">
                                <AlertCircle size={14} /> {errors.time}
                            </div>
                        )}

                        <div className="md:col-span-2">
                            {formData.taskId > 0 && isLinkedTaskLoading ? (
                                <div className="flex h-12 items-center rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-medium text-gray-500">
                                    Loading eligible people for the linked task…
                                </div>
                            ) : formData.taskId > 0 && linkedTaskLoadFailed ? (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                                    <div className="text-xs font-bold text-amber-900">
                                        Linked task details could not be loaded.
                                    </div>
                                    <div className="mt-1 text-[11px] font-medium text-amber-700">
                                        Existing attendees are preserved, but invite changes are disabled so participant restrictions cannot be bypassed.
                                    </div>
                                </div>
                            ) : (
                                <ParticipantSelectDropdown
                                    name="participants"
                                    label="Invite People"
                                    participantUserIds={formData.participantUserIds}
                                    individualUserIds={formData.individualUserIds}
                                    selectedTeamIds={formData.selectedTeamIds}
                                    externalUserEmails={formData.externalUserEmails}
                                    options={availableEmployeesToInvite}
                                    teams={teams}
                                    excludedUserIds={excludedInviteeUserIds}
                                    onChange={(data) => {
                                        const selectedWaivers =
                                            data.participantUserIds.filter((id) =>
                                                allWaiverParticipantIds.includes(
                                                    String(id).toLowerCase()
                                                )
                                            );
                                        const newActiveWaiverId =
                                            selectedWaivers.length > 0
                                                ? selectedWaivers[0]
                                                : null;

                                        if (
                                            formData.taskId &&
                                            linkedTask &&
                                            newActiveWaiverId
                                        ) {
                                            const taskParticipantId =
                                                getTaskParticipantId(linkedTask);

                                            if (
                                                taskParticipantId &&
                                                taskParticipantId !==
                                                    String(newActiveWaiverId).toLowerCase()
                                            ) {
                                                setLinkedTask(null);
                                                setFormData((previous) => ({
                                                    ...previous,
                                                    ...data,
                                                    taskId: 0
                                                }));
                                                return;
                                            }
                                        }

                                        setFormData((previous) => ({
                                            ...previous,
                                            ...data
                                        }));
                                    }}
                                />
                            )}

                            {formData.taskId > 0 && linkedTask && !isLinkedTaskLoading && (
                                <div className="mt-2 ml-1 flex items-center gap-1.5 text-xs font-semibold text-[#7E57C2]">
                                    <Info size={14} />
                                    List restricted to staff and {lockedTaskOwnerName} due to selected task.
                                </div>
                            )}
                        </div>

                        <div className="md:col-span-2">
                            <AsyncTaskDropdown
                                authToken={token}
                                selectedTask={linkedTask}
                                participantId={activeWaiverParticipantId || null}
                                onChange={(task) => {
                                    setLinkedTask(task);
                                    setLinkedTaskLoadFailed(false);

                                    const newTaskId = task ? Number(task.id) : 0;

                                    setFormData((previous) => {
                                        let participantUserIds = [
                                            ...previous.participantUserIds
                                        ];

                                        if (task) {
                                            const taskParticipantId =
                                                getTaskParticipantId(task);

                                            if (taskParticipantId) {
                                                participantUserIds =
                                                    participantUserIds.filter(
                                                        (id) => {
                                                            const normalizedId =
                                                                String(id).toLowerCase();
                                                            const isWaiverParticipant =
                                                                allWaiverParticipantIds.includes(
                                                                    normalizedId
                                                                );

                                                            return (
                                                                !isWaiverParticipant ||
                                                                normalizedId ===
                                                                    taskParticipantId
                                                            );
                                                        }
                                                    );
                                            }
                                        }

                                        return {
                                            ...previous,
                                            taskId: newTaskId,
                                            participantUserIds,
                                            individualUserIds: participantUserIds,
                                            selectedTeamIds: []
                                        };
                                    });
                                }}
                            />

                            {activeWaiverParticipantId && !formData.taskId && (
                                <div className="mt-2 ml-1 flex items-center gap-1.5 text-xs font-semibold text-[#7E57C2]">
                                    <Info size={14} />
                                    Task search is restricted to {activeParticipantName}.
                                </div>
                            )}
                        </div>

                        <div className="calendar-notes md:col-span-2 relative">
                            <CustomInput
                                icon={Info}
                                name="description"
                                type="textarea"
                                label="Internal Notes"
                                placeholder="Agenda or details..."
                                value={formData.description}
                                onChange={(e) => {
                                    if (e.target.value.length <= MAX_NOTES_LENGTH) {
                                        setFormData({ ...formData, description: e.target.value });
                                    }
                                }}
                                borderBotttom
                            />
                            <div className={`absolute bottom-2 right-4 text-[10px] font-bold ${formData.description.length >= MAX_NOTES_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                                {formData.description.length} / {MAX_NOTES_LENGTH}
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-4 pt-6 border-t border-gray-100">
                        <button type="button" onClick={onClose} className="flex-1 px-8 py-3.5 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-all duration-200">
                            Cancel
                        </button>
                        <button type="submit" disabled={!isDirty || loading || !!errors.time} className="flex-1 px-8 py-3.5 bg-[#7E57C2] text-white font-bold rounded-xl hover:bg-[#6a4aa8] shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-200 active:scale-[0.98]">
                            {loading ? "Saving..." : "Save Changes"}
                            {!loading && <CheckCircle2 size={18} />}
                        </button>
                    </div>
                </form>
            </div>

            <ScopeModal
                isOpen={showEditScopeModal}
                onClose={() => { setShowEditScopeModal(false); setPendingFormData(null); }}
                onSelectScope={(scope) => executeSave(scope, pendingFormData)}
                action="edit"
            />
        </>
    );
}
